import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, qs } from "@/lib/api";
import { KpiCard, SectionTitle } from "@/components/Kpi";
import { AzzeraFiltri, FilterBar, useFiltersMeta } from "@/components/Filters";
import { COLORS, ComparisonChart } from "@/components/Charts";
import { eurShort, num } from "@/lib/format";

const ANNO = 2026;
const SVILUPPO = "#93C5FD";

/** Portafoglio e Sviluppo: caricato e liquidato divisi tra convenzionati consolidati e nuovi. */
export default function PortafoglioSviluppo() {
  const { slug } = useParams();
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: ANNO, ...(slug ? { commerciale: slug } : {}) });
  const [d, setD] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [reset, setReset] = useState(0);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);
  useEffect(() => {
    setFilters((f) => ({ ...f, commerciale: slug || "" }));
  }, [slug]);
  useEffect(() => {
    api.get(`/dashboard/portafoglio-sviluppo?${qs(filters)}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [filters]);

  const t = d?.totali || {};
  const serie = d?.serie || [];
  const etichetta = { fill: "#FFFFFF", fontSize: 11, fontWeight: 600 };
  const chiavi = (base, colore) => [
    { key: `${base}_prec`, name: `${ANNO - 1}`, color: COLORS.grigio, stackId: "prec", labelStyle: etichetta },
    { key: `${base}_portafoglio`, name: "Portafoglio", color: colore, stackId: "ps", labelStyle: etichetta },
    { key: `${base}_sviluppo`, name: "Sviluppo", color: SVILUPPO, stackId: "ps", labelStyle: { ...etichetta, fill: "#0F172A" } },
  ];
  const quota = (a, b) => (a + b ? `${num(b / (a + b) * 100, 1)}% sviluppo` : "n/d");

  return (
    <div data-testid="portafoglio-sviluppo-page">
      <SectionTitle sub={`Portafoglio consolidato (convenzionati attivi fino al 2025) e sviluppo (attivati dal 1° gennaio 2026) · ${ANNO}${
        d?.dealer_sviluppo ? ` · ${num(d.dealer_sviluppo)} convenzionati in sviluppo` : ""}`}
        right={
          <a href={`/api/report/portafoglio-sviluppo.pdf?${qs(filters)}`} data-testid="btn-report-portafoglio-pdf"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001480] transition-colors duration-200">
            Scarica report PDF
          </a>
        }>
        Portafoglio e Sviluppo
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3">
        <FilterBar key={reset} meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={slug ? ["settore", "dealer"] : ["commerciale", "settore", "dealer"]} />
        <AzzeraFiltri onClick={() => {
          setFilters({ anno: ANNO, ...(slug ? { commerciale: slug } : { commerciale: "" }) });
          setReset((v) => v + 1);
        }} />
      </div>

      {!d ? (
        <div className="text-sm text-slate-500 mt-6" data-testid="ps-loading">Caricamento…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 my-6">
            <KpiCard testId="ps-kpi-caricate" label="Pratiche caricate · sviluppo" value={t.caricate_sviluppo}
              accent="caricato" sub={`Portafoglio ${num(t.caricate_portafoglio)}`}
              note={quota(t.caricate_portafoglio, t.caricate_sviluppo)} />
            <KpiCard testId="ps-kpi-caricato" label="Importo caricato · sviluppo" value={t.caricato_sviluppo}
              kind="eurShort" accent="caricato" sub={`Portafoglio ${eurShort(t.caricato_portafoglio)}`}
              note={quota(t.caricato_portafoglio, t.caricato_sviluppo)} />
            <KpiCard testId="ps-kpi-liquidate" label="Pratiche liquidate · sviluppo" value={t.liquidate_sviluppo}
              accent="liquidato" sub={`Portafoglio ${num(t.liquidate_portafoglio)}`}
              note={quota(t.liquidate_portafoglio, t.liquidate_sviluppo)} />
            <KpiCard testId="ps-kpi-liquidato" label="Importo liquidato · sviluppo" value={t.liquidato_sviluppo}
              kind="eurShort" accent="liquidato" sub={`Portafoglio ${eurShort(t.liquidato_portafoglio)}`}
              note={quota(t.liquidato_portafoglio, t.liquidato_sviluppo)} />
          </div>

          <div className="space-y-6">
            <ComparisonChart testId="ps-chart-caricate" height={320} labelFormat="int" legenda
              title={`Numero pratiche caricate · ${ANNO - 1} vs portafoglio e sviluppo ${ANNO}`}
              data={serie} keys={chiavi("caricate", COLORS.caricato)} emptyNote="nessuna pratica nel periodo" />
            <ComparisonChart testId="ps-chart-caricato" height={320} legenda
              title={`Importo caricato · ${ANNO - 1} vs portafoglio e sviluppo ${ANNO}`}
              data={serie} keys={chiavi("caricato", COLORS.caricato)} emptyNote="nessun importo nel periodo" />
            <ComparisonChart testId="ps-chart-liquidate" height={320} labelFormat="int" legenda
              title={`Numero pratiche liquidate · ${ANNO - 1} vs portafoglio e sviluppo ${ANNO}`}
              data={serie} keys={chiavi("liquidate", COLORS.liquidato)} emptyNote="nessuna liquidazione nel periodo" />
            <ComparisonChart testId="ps-chart-liquidato" height={320} legenda
              title={`Importo liquidato · ${ANNO - 1} vs portafoglio e sviluppo ${ANNO}`}
              data={serie} keys={chiavi("liquidato", COLORS.liquidato)} emptyNote="nessun importo liquidato nel periodo" />
          </div>
        </>
      )}
    </div>
  );
}
